import { useState, useEffect } from "react";
import { RotateCcw, Save, Loader2 } from "lucide-react";

export type ComponentKey = string;

export interface SignedComponent {
  c: ComponentKey;
  sign: 1 | -1;
}

export interface RatioFormula {
  numerator: SignedComponent[];
  denominator: SignedComponent[];
}

export interface SumFormula {
  sum: SignedComponent[];
}

export type KpiFormula = RatioFormula | SumFormula;

export function isRatioFormula(f: KpiFormula): f is RatioFormula {
  return (f as RatioFormula).numerator !== undefined;
}

export interface KpiFieldMetadata {
  key: string;
  label: string;
  unit: "count" | "percent" | "ms_to_seconds" | "ms_to_minutes";
  shape: "ratio" | "sum";
  componentScope: { numerator: ComponentKey[]; denominator?: ComponentKey[] };
  defaultFormula: KpiFormula;
}

interface Props {
  customerId: string;
  metadata: KpiFieldMetadata;
  componentLabels: Record<ComponentKey, string>;
  formula: KpiFormula;
  isOverridden: boolean;
  onSaved: (formula: KpiFormula) => void;
  onReset: () => void;
}

/** A signed-component checklist: each component appears once, checked = included with sign +1;
 *  a small "-" toggle next to a checked component flips it to sign -1 (subtract). Covers the
 *  common case (pure inclusion/exclusion) with one click, and signed subtraction with two. */
function ComponentChecklist({
  title,
  keys,
  labels,
  selected,
  onChange,
}: {
  title: string;
  keys: ComponentKey[];
  labels: Record<ComponentKey, string>;
  selected: SignedComponent[];
  onChange: (next: SignedComponent[]) => void;
}) {
  const find = (c: ComponentKey) => selected.find((s) => s.c === c);

  const toggleIncluded = (c: ComponentKey) => {
    const existing = find(c);
    if (existing) onChange(selected.filter((s) => s.c !== c));
    else onChange([...selected, { c, sign: 1 }]);
  };

  const toggleSign = (c: ComponentKey) => {
    onChange(selected.map((s) => (s.c === c ? { ...s, sign: s.sign === 1 ? -1 : 1 } : s)));
  };

  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-1">{title}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {keys.map((key) => {
          const entry = find(key);
          const included = Boolean(entry);
          return (
            <div key={key} className="flex items-center gap-1.5">
              <label className="flex items-center gap-1.5 cursor-pointer flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => toggleIncluded(key)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                />
                <span className={`text-xs truncate ${included ? "text-gray-800" : "text-gray-400"}`}>
                  {labels[key] ?? key}
                </span>
              </label>
              {included && (
                <button
                  type="button"
                  title={entry!.sign === 1 ? "Click to subtract instead" : "Click to add instead"}
                  onClick={() => toggleSign(key)}
                  className={`text-xs font-mono w-4 h-4 flex items-center justify-center rounded flex-shrink-0 ${
                    entry!.sign === 1 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}
                >
                  {entry!.sign === 1 ? "+" : "−"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Inline formula editor for one KPI v2 metric. Lets a user pick exactly which atomic call
 * components feed a metric's numerator/denominator (or its plain sum) — the general
 * "client picks the formula" mechanism, not a fixed on/off toggle for a few pre-chosen
 * combinations. Saves as a per-customer override; "Reset to default" clears it.
 */
export function KpiFormulaEditor({ customerId, metadata, componentLabels, formula, isOverridden, onSaved, onReset }: Props) {
  const [draft, setDraft] = useState<KpiFormula>(formula);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(formula);
  }, [formula]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(formula);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/kpi-metric-settings/${customerId}/${metadata.key}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ formula: draft }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to save formula");
      onSaved(json.data.formulas[metadata.key]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save formula");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setResetting(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/kpi-metric-settings/${customerId}/${metadata.key}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to reset formula");
      setDraft(metadata.defaultFormula);
      onReset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to reset formula");
    } finally {
      setResetting(false);
    }
  };

  const unitHint =
    metadata.unit === "percent" ? "shown as a %"
    : metadata.unit === "ms_to_seconds" ? "shown as minutes:seconds"
    : metadata.unit === "ms_to_minutes" ? "shown as minutes"
    : "shown as a count";

  return (
    <div className="mt-2 ml-6 p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Formula for <span className="font-medium text-gray-700">{metadata.label}</span> ({unitHint})
          {isOverridden && <span className="ml-2 text-amber-600 font-medium">Customized</span>}
        </p>
        {isOverridden && (
          <button
            type="button"
            onClick={resetToDefault}
            disabled={resetting}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            Reset to default
          </button>
        )}
      </div>

      {isRatioFormula(draft) ? (
        <div className="grid grid-cols-2 gap-4">
          <ComponentChecklist
            title="Numerator (top of the ratio)"
            keys={metadata.componentScope.numerator}
            labels={componentLabels}
            selected={draft.numerator}
            onChange={(next) => setDraft({ ...draft, numerator: next })}
          />
          <ComponentChecklist
            title="Denominator (bottom of the ratio)"
            keys={metadata.componentScope.denominator ?? []}
            labels={componentLabels}
            selected={draft.denominator}
            onChange={(next) => setDraft({ ...draft, denominator: next })}
          />
        </div>
      ) : (
        <ComponentChecklist
          title="Included in the total"
          keys={metadata.componentScope.numerator}
          labels={componentLabels}
          selected={draft.sum}
          onChange={(next) => setDraft({ sum: next })}
        />
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!isDirty || saving}
          className="text-xs font-medium px-2.5 py-1 rounded bg-blue-600 text-white disabled:opacity-40 flex items-center gap-1"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save formula
        </button>
        {isDirty && (
          <button
            type="button"
            onClick={() => setDraft(formula)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Discard changes
          </button>
        )}
      </div>
    </div>
  );
}
